import { INTEGRATION_TYPES } from '../integrations.constants';
import { readAwsBucketName } from './prevention-aws-bucket-name';
import {
  deriveAwsListenerArn,
  deriveConfigRecordName,
} from './prevention-config-identifiers';
import { DEFAULT_NETWORK_SOURCE_CIDR } from './prevention-guardrail-predicates';
import { resolveNetworkProviderProfile } from './network-provider-capabilities';
import {
  PREVENTION_CONTROL_TYPES,
  PreventionControlTypeSchema,
  type PreventionControlType,
  type PreventionProvider,
  type CloudProvider,
  type PreventionScopeType,
} from './prevention.schema';

type PreventionDeploymentContext = {
  scopeRef?: string | null;
  scopeType?: string | null;
  cloudProvider?: CloudProvider | null;
};

export type PreventionMergeAgentInput = Record<string, unknown>;

export const readPreventionMergeAgentInput = (
  value: unknown,
): PreventionMergeAgentInput | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }

  return Object.fromEntries(Object.entries(value));
};

const readString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const readPort = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isInteger(value)) {
    return null;
  }

  if (value < 1 || value > 65535) {
    return null;
  }

  return value;
};

const readProtocol = (
  value: unknown,
): 'tcp' | 'udp' | 'icmp' | 'all' | null => {
  if (
    value === 'tcp' ||
    value === 'udp' ||
    value === 'icmp' ||
    value === 'all'
  ) {
    return value;
  }

  return null;
};

const readDirection = (
  value: unknown,
): 'inbound' | 'outbound' | 'both' | null => {
  if (value === 'inbound' || value === 'outbound' || value === 'both') {
    return value;
  }

  return null;
};

const readNetworkScopeType = (
  provider: PreventionProvider,
): PreventionScopeType | null => {
  const requiredScopeType =
    resolveNetworkProviderProfile(provider)?.requiredScopeType;

  if (
    requiredScopeType === 'securityGroup' ||
    requiredScopeType === 'nacl' ||
    requiredScopeType === 'firewall' ||
    requiredScopeType === 'nsg'
  ) {
    return requiredScopeType;
  }

  return null;
};

const buildNetworkServerOwnedValues = ({
  provider,
  deploymentContext,
  enrichedTarget,
}: {
  provider: PreventionProvider;
  deploymentContext?: PreventionDeploymentContext | null;
  enrichedTarget?: Record<string, unknown>;
}): PreventionMergeAgentInput => {
  const scopeType = readNetworkScopeType(provider);
  const scopeId =
    readString(deploymentContext?.scopeRef) ??
    readString(enrichedTarget?.scopeId);
  const targetIp = readString(enrichedTarget?.targetIp);
  const targetPort = readPort(enrichedTarget?.targetPort);
  const protocol = readProtocol(enrichedTarget?.protocol) ?? 'tcp';
  const direction =
    readDirection(enrichedTarget?.direction) === 'outbound'
      ? 'outbound'
      : 'inbound';

  return {
    direction,
    ...(scopeType ? { scopeType } : {}),
    ...(scopeId ? { scopeId } : {}),
    ...(targetIp ? { targetIp } : {}),
    ...(targetPort != null ? { targetPort } : {}),
    protocol,
  };
};

const buildIpsServerOwnedValues = ({
  enrichedTarget,
}: {
  enrichedTarget?: Record<string, unknown>;
}): PreventionMergeAgentInput => {
  const targetIp = readString(enrichedTarget?.targetIp);
  const cidr = readString(enrichedTarget?.cidr);
  const sensorScope = readString(enrichedTarget?.sensorScope);
  const protocol = readProtocol(enrichedTarget?.protocol) ?? 'tcp';
  const direction = readDirection(enrichedTarget?.direction) ?? 'inbound';

  return {
    direction,
    protocol,
    ...(targetIp ? { targetIp } : {}),
    ...(cidr ? { cidr } : {}),
    ...(sensorScope ? { sensorScope } : {}),
  };
};

const POLICY_DETAIL_KEYS = [
  'sslPolicy',
  'recordType',
  'recordValue',
  'recordValues',
  'ttl',
  'mechanism',
  'selector',
] as const;

const IDENTIFIER_DETAIL_KEYS = new Set([
  'hostedZoneId',
  'recordName',
  'listenerArn',
  'certificateArn',
  'subscriptionId',
  'resourceGroup',
  'accountName',
  'storageAccountName',
  'bucketName',
]);

const pickPolicyDetails = (
  details?: Record<string, unknown>,
): Record<string, unknown> => {
  if (!details) {
    return {};
  }

  return Object.fromEntries(
    POLICY_DETAIL_KEYS.flatMap((key) => {
      const value = details[key];

      return value === undefined ? [] : [[key, value]];
    }),
  );
};

const buildConfigServerOwnedValues = ({
  enrichedTarget,
  cloudProvider,
  change,
  details,
  deploymentContext,
}: {
  enrichedTarget?: Record<string, unknown>;
  cloudProvider?: CloudProvider | null;
  change?: string;
  details?: Record<string, unknown>;
  deploymentContext?: PreventionDeploymentContext | null;
}): PreventionMergeAgentInput => {
  const target = readString(enrichedTarget?.target);
  const mergedDetails = pickPolicyDetails(details);

  if (cloudProvider === 'aws' && change === 'disablePublicAccess' && target) {
    const bucketName = readAwsBucketName({ target });

    if (bucketName) {
      mergedDetails.bucketName = bucketName;
    }
  }

  if (change === 'enforceTls') {
    const listenerArn = deriveAwsListenerArn({
      target,
      scopeRef: deploymentContext?.scopeRef,
      enrichedListenerArn: readString(enrichedTarget?.listenerArn),
    });

    if (listenerArn) {
      mergedDetails.listenerArn = listenerArn;
    }
  }

  if (change === 'dnsRecord' || change === 'emailAuth') {
    const recordName = deriveConfigRecordName({ change, target, details });

    if (recordName) {
      mergedDetails.recordName = recordName;
    }
  }

  return {
    ...(target ? { target } : {}),
    ...(Object.keys(mergedDetails).length > 0
      ? { details: mergedDetails }
      : {}),
  };
};

export const buildPreventionServerOwnedValues = ({
  provider,
  controlType,
  deploymentContext,
  enrichedTarget,
  cloudProvider,
  agentInput,
}: {
  provider: PreventionProvider;
  controlType: PreventionControlType;
  deploymentContext?: PreventionDeploymentContext | null;
  enrichedTarget?: Record<string, unknown>;
  cloudProvider?: CloudProvider | null;
  agentInput?: PreventionMergeAgentInput;
}): PreventionMergeAgentInput => {
  switch (controlType) {
    case PREVENTION_CONTROL_TYPES.NETWORK:
      return buildNetworkServerOwnedValues({
        provider,
        deploymentContext,
        enrichedTarget,
      });
    case PREVENTION_CONTROL_TYPES.IPS:
      return buildIpsServerOwnedValues({ enrichedTarget });
    case PREVENTION_CONTROL_TYPES.CONFIG: {
      const change = readString(agentInput?.change);
      const details =
        agentInput?.details &&
        typeof agentInput.details === 'object' &&
        !Array.isArray(agentInput.details)
          ? Object.fromEntries(Object.entries(agentInput.details))
          : undefined;

      return buildConfigServerOwnedValues({
        enrichedTarget,
        cloudProvider,
        ...(change ? { change } : {}),
        details,
        deploymentContext,
      });
    }

    case PREVENTION_CONTROL_TYPES.WAF:
      return {};
    default:
      return {};
  }
};

const collectUnresolvableNetworkFields = ({
  provider,
  values,
}: {
  provider: PreventionProvider;
  values: PreventionMergeAgentInput;
}): string[] => {
  const missing: string[] = [];

  if (!readString(values.targetIp)) {
    missing.push('targetIp');
  }

  if (readPort(values.targetPort) == null) {
    missing.push('targetPort');
  }

  const scopeType = readNetworkScopeType(provider);

  if (scopeType && values.scopeType !== scopeType) {
    missing.push('scopeType');
  }

  return missing;
};

const collectUnresolvableConfigFields = ({
  values,
  cloudProvider,
}: {
  values: PreventionMergeAgentInput;
  cloudProvider?: CloudProvider | null;
}): string[] => {
  const missing: string[] = [];

  if (!readString(values.target)) {
    missing.push('target');
  }

  const change = readString(values.change);
  const details: Record<string, unknown> =
    values.details &&
    typeof values.details === 'object' &&
    !Array.isArray(values.details)
      ? (values.details as Record<string, unknown>)
      : {};

  if (change === 'enforceTls' && !readString(details.listenerArn)) {
    missing.push('listenerArn');
  }

  if (
    cloudProvider === 'aws' &&
    change === 'disablePublicAccess' &&
    !readString(details.bucketName)
  ) {
    missing.push('bucketName');
  }

  if (
    (change === 'dnsRecord' || change === 'emailAuth') &&
    !readString(details.recordName)
  ) {
    missing.push('recordName');
  }

  return missing;
};

const collectControlTypeMismatch = ({
  controlType,
  agentInput,
}: {
  controlType: PreventionControlType;
  agentInput: PreventionMergeAgentInput;
}): string[] => {
  const agentControlType = readString(agentInput.controlType);

  if (agentControlType && agentControlType !== controlType) {
    return ['controlType'];
  }

  return [];
};

export type MergePreventionServerOwnedFieldsResult =
  | { success: true; merged: PreventionMergeAgentInput }
  | { success: false; unresolvableFields: string[] };

export const mergePreventionServerOwnedFields = ({
  provider,
  controlType,
  deploymentContext,
  enrichedTarget,
  cloudProvider,
  agentInput,
}: {
  provider: PreventionProvider;
  controlType: PreventionControlType;
  deploymentContext?: PreventionDeploymentContext | null;
  enrichedTarget?: Record<string, unknown>;
  cloudProvider?: CloudProvider | null;
  agentInput: PreventionMergeAgentInput;
}): MergePreventionServerOwnedFieldsResult => {
  const parsedControlType = PreventionControlTypeSchema.safeParse(controlType);

  if (!parsedControlType.success) {
    return { success: false, unresolvableFields: ['controlType'] };
  }

  const controlTypeMismatch = collectControlTypeMismatch({
    controlType: parsedControlType.data,
    agentInput,
  });

  if (controlTypeMismatch.length > 0) {
    return { success: false, unresolvableFields: controlTypeMismatch };
  }

  const serverOwned = buildPreventionServerOwnedValues({
    provider,
    controlType: parsedControlType.data,
    deploymentContext,
    enrichedTarget,
    cloudProvider,
    agentInput,
  });

  const merged: PreventionMergeAgentInput = {
    ...agentInput,
    ...serverOwned,
    controlType: parsedControlType.data,
  };

  if (
    parsedControlType.data === PREVENTION_CONTROL_TYPES.CONFIG &&
    serverOwned.details &&
    typeof serverOwned.details === 'object' &&
    !Array.isArray(serverOwned.details)
  ) {
    const agentDetails =
      agentInput.details &&
      typeof agentInput.details === 'object' &&
      !Array.isArray(agentInput.details)
        ? agentInput.details
        : {};

    merged.details = {
      ...Object.fromEntries(
        Object.entries(agentDetails).filter(
          ([key]) => !IDENTIFIER_DETAIL_KEYS.has(key),
        ),
      ),
      ...serverOwned.details,
    };
  }

  if (parsedControlType.data === PREVENTION_CONTROL_TYPES.NETWORK) {
    if (!readString(merged.cidr)) {
      merged.cidr = DEFAULT_NETWORK_SOURCE_CIDR;
    }

    const unresolvable = collectUnresolvableNetworkFields({
      provider,
      values: merged,
    });

    if (unresolvable.length > 0) {
      return { success: false, unresolvableFields: unresolvable };
    }
  }

  if (parsedControlType.data === PREVENTION_CONTROL_TYPES.CONFIG) {
    const unresolvable = collectUnresolvableConfigFields({
      values: merged,
      cloudProvider,
    });

    if (unresolvable.length > 0) {
      return { success: false, unresolvableFields: unresolvable };
    }
  }

  return { success: true, merged };
};

export const isKnownNetworkPreventionProvider = (
  provider: PreventionProvider,
): boolean =>
  provider === INTEGRATION_TYPES.AWS_SECURITY_GROUP ||
  provider === INTEGRATION_TYPES.GCP_FIREWALL ||
  provider === INTEGRATION_TYPES.AZURE_NSG;
